import { Trans } from '@lingui/react/macro';
import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import { getHttpClientConfig } from '@luminovo/http-client';
import { Box } from '@mui/material';
import React from 'react';

export function VisuallyHidden({ children }: { children: React.ReactNode }): JSX.Element {
    return (
        <span
            style={{
                position: 'absolute',
                border: 0,
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                wordWrap: 'normal',
            }}
        >
            {children}
        </span>
    );
}

export function KeyboardBadge({ children }: { children: React.ReactNode }): JSX.Element {
    return (
        <Box
            component="kbd"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '20px',
                height: '20px',
                padding: '0 5px',
                backgroundColor: colorSystem.neutral[1],
                border: `1px solid ${colorSystem.neutral[3]}`,
                borderRadius: '4px',
                fontSize: '11px',
                fontFamily: 'inherit',
                fontWeight: 500,
                color: colorSystem.neutral[6],
            }}
        >
            {children}
        </Box>
    );
}

export function HintButton({
    onClick,
    badge,
    label,
}: {
    onClick?: () => void;
    badge: React.ReactNode;
    label: React.ReactNode;
}): JSX.Element {
    return (
        <Box
            component={onClick ? 'button' : 'span'}
            onClick={onClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 6px',
                margin: '-4px -6px',
                border: 'none',
                background: 'transparent',
                borderRadius: '4px',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background 0.1s ease',
                '&:hover': onClick
                    ? {
                          backgroundColor: colorSystem.neutral[2],
                      }
                    : {},
            }}
        >
            <KeyboardBadge>{badge}</KeyboardBadge>
            <Text variant="body-small" color={colorSystem.neutral[6]}>
                {label}
            </Text>
        </Box>
    );
}

export function CommandPaletteFooter({
    onClose,
    isMac,
    shortcutKey = 'P',
    showOpenInNewTab = false,
}: {
    onClose: () => void;
    isMac: boolean;
    shortcutKey?: string;
    showOpenInNewTab?: boolean;
}): JSX.Element {
    return (
        <Box
            sx={{
                padding: '10px 16px',
                borderTop: `1px solid ${colorSystem.neutral[2]}`,
            }}
        >
            <Flexbox
                gap="8px"
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Flexbox
                    gap="12px"
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <HintButton badge="↑↓" label={<Trans>navigate</Trans>} />
                    <HintButton badge="↵" label={<Trans>open</Trans>} />
                    {showOpenInNewTab && (
                        <HintButton
                            badge={
                                <Flexbox
                                    gap="2px"
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <span style={{ fontSize: '9px' }}>{isMac ? '⌘' : 'Ctrl'}</span>
                                    <span>↵</span>
                                </Flexbox>
                            }
                            label={<Trans>new tab</Trans>}
                        />
                    )}
                    <HintButton badge="esc" label={<Trans>close</Trans>} onClick={onClose} />
                </Flexbox>
                <Flexbox
                    gap="2px"
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <KeyboardBadge>{isMac ? '⌘' : 'Ctrl'}</KeyboardBadge>
                    <KeyboardBadge>{shortcutKey}</KeyboardBadge>
                </Flexbox>
            </Flexbox>
        </Box>
    );
}

/** Attributes for the palettes' Sentry metrics. */
export function attrs(
    telemetry: Record<string, string | number | boolean> | undefined,
    extra?: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> | undefined {
    return { host: getHttpClientConfig().hostName, ...telemetry, ...extra };
}
